import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { IBoletim, IFiltroBoletins, ISubfiltros, PageBoletins } from '../types';
import BoletinsService from '@/views/boletins/service';
import SdsSnackbarQueue from '@ungp/sds-snackbar-queue';
import {
  transformarDadosSubFilter,
  verificarSePossuiItensSelecionadosOuExpandidos,
} from '@/utils/transform';
import { cloneDeep } from '@apollo/client/utilities';
import { processarAno } from '@/utils/util';
import { GET_BOLETINS } from '@/queries';
import { useLazyQuery } from '@vue/apollo-composable';
import { FL_SIM } from '@/constants';

export const ConsultaBoletinsStore = defineStore(
  'consultaBoletinsStore',
  () => {
    const pageBoletins = ref<PageBoletins>({});

    const pageSubfiltros = ref<ISubfiltros[]>([]);

    const pageSubfiltrosOld = ref<ISubfiltros[] | undefined>(undefined);

    const pagina = ref(1);

    const filtroAvancadoExpandido = ref(false);

    const filtroBoletins = ref<IFiltroBoletins>({
      dePesquisaTermos: null,
      ordenacao: 'nuAno DESC',
      expandirPesquisaAvancada: false,
      boletins: [],
    });

    const tiposBoletins = ref<IBoletim[]>([]);

    const showFilter = ref(false);

    const boletins = computed<IBoletim[]>(
      () => pageBoletins.value.boletins || []
    );

    const boletinsSelecionados = ref<IBoletim[]>([]);

    const codigosBoletinsSelecionadosComDocumento = computed(() =>
      boletinsSelecionados.value
        .filter(
          (boletim) =>
            boletim.cdDocumento || boletim.flJurisprudencia !== FL_SIM
        )
        .map((boletim) => boletim.cdBoletim)
    );

    const codigosBoletinsSelecionadosSemDocumento = computed(() =>
      boletinsSelecionados.value
        .filter(
          (boletim) =>
            !boletim.cdDocumento || boletim.flJurisprudencia === FL_SIM
        )
        .map((boletim) => boletim.cdBoletim)
    );

    const subFiltrosCategorias = computed(() => {
      if (pageSubfiltros.value) {
        return transformarDadosSubFilter(pageSubfiltros.value);
      }
      return {
        classes: [],
        relatores: [],
        comarcas: [],
        orgaosJulgadores: [],
        revistas: [],
        anos: [],
        prolatores: [],
      };
    });

    const totalHits = computed(() => pageBoletins.value.totalHits || 0);

    const limiteTotalHists = 10000;

    const totalElementos = computed(() => {
      if (totalHits.value > limiteTotalHists) return limiteTotalHists;
      return totalHits.value;
    });

    const temBoletinsTiposDiferentes = computed(() => {
      const temBoletinsSemDocumentos = boletins.value?.some(
        (el: IBoletim) => !el.cdDocumento
      );
      const temBoletinsComDocumentos = boletins.value?.some(
        (el: IBoletim) => !!el.cdDocumento
      );
      return temBoletinsSemDocumentos && temBoletinsComDocumentos;
    });

    const temFiltroBoletimComFlagJurisprudencia = computed(() =>
      filtroBoletins.value.boletins.some(
        (boletim) => boletim.flJurisprudencia === FL_SIM
      )
    );

    const tipoBoletimSelecionadoParaImpressao = computed<
      'COM_DOCUMENTO' | 'SEM_DOCUMENTO' | null
    >(() => {
      if (codigosBoletinsSelecionadosComDocumento.value.length) {
        return 'COM_DOCUMENTO';
      }

      if (codigosBoletinsSelecionadosSemDocumento.value.length) {
        return 'SEM_DOCUMENTO';
      }

      return null;
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params = computed<any>(() => {
      const {
        dePesquisaTermos,
        dtPublicacaoInicio,
        dtPublicacaoFim,
        boletins,
        relator,
        dtJulgamentoInicio,
        dtJulgamentoFim,
        ordenacao,
      } = filtroBoletins.value;

      const cdsTipoBoletim = boletins.map((item) => item.cdTipoBoletim);

      return {
        cdsTipoBoletim,
        dePesquisaTermos,
        dtJulgamentoInicio: dtJulgamentoInicio
          ? new Date(dtJulgamentoInicio).getTime()
          : '',
        dtJulgamentoFim: dtJulgamentoFim
          ? new Date(dtJulgamentoFim).getTime()
          : '',
        dtPublicacaoInicio: dtPublicacaoInicio
          ? new Date(dtPublicacaoInicio).getTime()
          : '',
        dtPublicacaoFim: dtPublicacaoFim
          ? new Date(dtPublicacaoFim).getTime()
          : '',
        flEmenta: false,
        flInteiroTeor: false,
        flPesquisaPlurais: 'N',
        legislativasNormativas: [
          {
            tipoNorma: {},
            nuNorma: '',
            deArtigo: '',
            deParagrafo: '',
            deInciso: '',
            deAlinea: '',
            deNumero: '',
          },
        ],
        numPag: pagina.value - 1,
        ordenacao,
        subFilter: subFiltrosCategorias.value,
        subFiltrosOld: pageSubfiltrosOld.value || undefined,
        tipoBoletimSelected: boletins ? boletins : [],
        relatorSelected: relator ? relator : [],
      };
    });

    const buscaInicialBoletins = async () => {
      const { load, variables } = useLazyQuery(GET_BOLETINS);
      variables.value = { nmTipoBoletim: '' };
      tiposBoletins.value = (await load()).tipoBoletins;
    };

    // Actions
    const consultaBoletins = async (initialPagination = false) => {
      pageBoletins.value = {};
      const {
        dePesquisaTermos,
        dtPublicacaoInicio,
        dtPublicacaoFim,
        dtJulgamentoInicio,
        dtJulgamentoFim,
        boletins,
      } = filtroBoletins.value;

      if (
        !dePesquisaTermos &&
        !dtPublicacaoInicio &&
        !dtPublicacaoFim &&
        !dtJulgamentoInicio &&
        !dtJulgamentoFim &&
        !boletins
      ) {
        SdsSnackbarQueue.warn('Informe pelo menos um filtro para a consulta');
        return;
      }

      if (initialPagination) {
        pageSubfiltros.value = [];
        pageSubfiltrosOld.value = undefined;
        showFilter.value = false;
        boletinsSelecionados.value = [];
      }

      try {
        const response = await BoletinsService.consultarBoletins(params.value);

        if (response.hasError) {
          const msgErro = response.error?.message;
          SdsSnackbarQueue.error(
            msgErro ? msgErro : 'Não foi possível buscar os registros'
          );
          return;
        }

        pageSubfiltrosOld.value = cloneDeep(pageSubfiltros.value);

        pageBoletins.value = response.content!;

        const subfiltrosAtualizados =
          pageBoletins.value.subfiltros || undefined;

        if (pageSubfiltrosOld.value && pageSubfiltrosOld.value.length > 0) {
          pageSubfiltros.value = verificarSePossuiItensSelecionadosOuExpandidos(
            subfiltrosAtualizados,
            pageSubfiltrosOld.value
          );
        } else {
          pageSubfiltros.value = subfiltrosAtualizados!;
        }
      } catch (error) {
        SdsSnackbarQueue.error('Não foi possível buscar os registros');
      }
    };

    const limparTodosFiltroBoletins = () => {
      pageBoletins.value = {};
      pageSubfiltros.value = [];
      pageSubfiltrosOld.value = undefined;
      filtroBoletins.value = {
        dePesquisaTermos: null,
        ordenacao: 'nuAno DESC',
        expandirPesquisaAvancada: false,
        boletins: [],
      };
    };

    const download = () => {
      switch (tipoBoletimSelecionadoParaImpressao.value) {
        case 'COM_DOCUMENTO':
          downloadBoletimComDocumento(
            codigosBoletinsSelecionadosComDocumento.value
          );
          break;
        case 'SEM_DOCUMENTO':
          BoletinsService.downloadBoletimSemDocumento(
            codigosBoletinsSelecionadosSemDocumento.value
          );
          break;
      }
    };

    const downloadBoletimComDocumento = async (
      codigosDocumentos: Array<number | undefined>
    ) => {
      try {
        const blob = await BoletinsService.downloadBoletimComDocumento(
          codigosDocumentos
        );

        if (!blob) {
          throw new Error();
        }

        const data = blob.slice(0, blob.size, 'application/pdf');
        const file = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = file;
        a.download = 'relatorio-boletim.pdf';
        document.body.appendChild(a);
        a.click();
      } catch (error) {
        SdsSnackbarQueue.error(
          String(error) || 'Não foi possível realizar o download'
        );
        return;
      }
    };

    const setDataPublicacaoInicio = (ano: number) => {
      try {
        const data = processarAno(ano);
        filtroBoletins.value = {
          ...filtroBoletins.value,
          dtPublicacaoInicio: data,
        };
      } catch (error) {
        SdsSnackbarQueue.warn(error as string);
        filtroBoletins.value = {
          ...filtroBoletins.value,
          dtPublicacaoInicio: undefined,
        };
      }
    };

    const setDataPublicacaoFim = (ano: number) => {
      try {
        const data = processarAno(ano);
        filtroBoletins.value = {
          ...filtroBoletins.value,
          dtPublicacaoFim: data,
        };
      } catch (error) {
        SdsSnackbarQueue.warn(error as string);
        filtroBoletins.value = {
          ...filtroBoletins.value,
          dtPublicacaoFim: undefined,
        };
      }
    };

    return {
      pageBoletins,
      pageSubfiltros,
      tiposBoletins,
      pageSubfiltrosOld,
      pagina,
      totalHits,
      totalElementos,
      filtroBoletins,
      subFiltrosCategorias,
      temBoletinsTiposDiferentes,
      tipoBoletimSelecionadoParaImpressao,
      temFiltroBoletimComFlagJurisprudencia,
      filtroAvancadoExpandido,
      showFilter,
      boletins,
      boletinsSelecionados,
      consultaBoletins,
      limparTodosFiltroBoletins,
      download,
      setDataPublicacaoInicio,
      setDataPublicacaoFim,
      buscaInicialBoletins,
    };
  }
);
